import React, { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import v4 from 'uuid/v4'
import { DV_TAB_PROJECT_STATISTICS, DV_TAB_USER_STATISTICS } from '../../../utils/TabNavigationConstants'
import URLsPeople, { URL_PEOPLE_DETAILS_STATISTICS } from '../../../URLSystem/People/URLsPeople'
import URLsProjects, { URL_PROJECT_DETAILS_STATISTICS } from '../../../URLSystem/Projects/URLsProjects'
import {
    getDateRangesTimestamps,
    getFilterOption,
    getStatisticsFilterData,
    STATISTIC_RANGE_CURRENT_MONTH,
} from '../../StatisticsView/statisticsHelper'
import { getEstimationTypeByProjectId } from '../../../utils/EstimationHelper'
import Backend from '../../../utils/BackendBridge'
import StatisticsSection from '../../StatisticsView/StatisticsSection/StatisticsSection'
import ProjectHelper from '../../SettingsView/ProjectsSettings/ProjectHelper'
import store from '../../../redux/store'

export default function StatisticsView({ projectId, userId, initialFilterData }) {
    const selectedTab = useSelector(state => state.selectedNavItem)
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const statisticsSelectedUsersIds = useSelector(state => state.loggedUser.statisticsSelectedUsersIds[projectId])
    const [filterData, setFilterData] = useState(
        () => initialFilterData || getStatisticsFilterData(STATISTIC_RANGE_CURRENT_MONTH)
    )
    const statisticsDataRef = useRef({})
    const allStatisticsDataRef = useRef({})
    const [statisticsData, setStatisticsData] = useState({})
    const [allStatisticsData, setAllStatisticsData] = useState({})
    const [happinessEntries, setHappinessEntries] = useState([])
    const [okrEntries, setOkrEntries] = useState([])

    const usersToShow = statisticsSelectedUsersIds ? statisticsSelectedUsersIds : [loggedUserId]
    const project = ProjectHelper.getProjectById(projectId)

    const writeBrowserURL = () => {
        const data = { projectId, userId }
        if (selectedTab === DV_TAB_USER_STATISTICS) {
            URLsPeople.push(URL_PEOPLE_DETAILS_STATISTICS, data, projectId, userId)
        } else if (selectedTab === DV_TAB_PROJECT_STATISTICS) {
            URLsProjects.push(URL_PROJECT_DETAILS_STATISTICS, data, projectId, userId)
        }
    }

    const updateAllStatistics = (projectId, statistics, allStatistics, userId) => {
        // Cumulative statistics
        statisticsDataRef.current[userId] = statistics
        setStatisticsData({ ...statisticsDataRef.current, [userId]: statistics })

        // All dates statistics
        const finalStatistics = { ...allStatistics }

        for (let date in allStatistics.allDoneTime) {
            finalStatistics.allDoneTime[date] = Number((allStatistics.allDoneTime[date] / 60).toFixed(2))
        }

        allStatisticsDataRef.current[userId] = finalStatistics
        setAllStatisticsData({ ...allStatisticsDataRef.current, [userId]: finalStatistics })
    }

    const updateStatisticsFilter = (filterOption, customDateRange) => {
        setFilterData(getStatisticsFilterData(filterOption, customDateRange))
    }

    useEffect(() => {
        writeBrowserURL()
    }, [])

    useEffect(() => {
        if (initialFilterData) {
            setFilterData(initialFilterData)
        }
    }, [JSON.stringify(initialFilterData)])

    useEffect(() => {
        const { timestamp1, timestamp2 } = getDateRangesTimestamps(filterData)
        const estimationType = getEstimationTypeByProjectId(projectId)
        const watcherKey = v4()
        statisticsDataRef.current = {}
        allStatisticsDataRef.current = {}
        let array = []
        usersToShow.forEach(id => {
            array.push(id)
            Backend.watchAllUserStatisticsByRange(
                projectId,
                estimationType,
                id,
                timestamp1,
                timestamp2,
                id + watcherKey,
                updateAllStatistics
            )
        })

        return () => {
            array.forEach(id => {
                Backend.unwatch(id + watcherKey)
            })
        }
    }, [JSON.stringify(filterData), JSON.stringify(usersToShow)])

    useEffect(() => {
        const { timestamp1, timestamp2 } = getDateRangesTimestamps(filterData)
        const watcherId = v4()
        const happinessWatcherKey = `project_happiness_stats_${projectId}_${watcherId}`
        const okrWatcherKey = `project_okr_stats_${projectId}_${watcherId}`
        Backend.watchProjectHappinessByRange(
            projectId,
            loggedUserId,
            timestamp1,
            timestamp2,
            happinessWatcherKey,
            (id, entries) => {
                setHappinessEntries(entries)
            }
        )
        Backend.watchProjectOKRProgressByRange(projectId, timestamp1, timestamp2, okrWatcherKey, (id, entries) => {
            setOkrEntries(entries)
        })

        return () => {
            Backend.unwatch(happinessWatcherKey)
            Backend.unwatch(okrWatcherKey)
        }
    }, [JSON.stringify(filterData), projectId, loggedUserId])

    const filter = getFilterOption(filterData)

    const statisticsDataReduced = Object.values(statisticsData).reduce(
        (prev, curr) => ({
            doneTasks: prev.doneTasks + curr.doneTasks,
            donePoints: prev.donePoints + curr.donePoints,
            doneTime: prev.doneTime + curr.doneTime,
            gold: prev.gold + curr.gold,
            xp: prev.xp + curr.xp,
        }),
        { doneTasks: 0, donePoints: 0, doneTime: 0, gold: 0, xp: 0 }
    )

    const allStatisticsDataReduced = Object.values(allStatisticsData).reduce(
        (prev, curr) => ({
            allDoneTasks: { ...prev.allDoneTasks, ...curr.allDoneTasks },
            allDonePoints: { ...prev.allDonePoints, ...curr.allDonePoints },
            allDoneTime: { ...prev.allDoneTime, ...curr.allDoneTime },
            allXp: { ...prev.allXp, ...curr.allXp },
            allGold: { ...prev.allGold, ...curr.allGold },
        }),
        { allDoneTasks: {}, allDonePoints: {}, allDoneTime: {}, allXp: {}, allGold: {} }
    )

    // Calculate money earned for selected users
    const calculateMoneyEarned = () => {
        if (!project?.hourlyRatesData) return 0

        const { currency, hourlyRates } = project.hourlyRatesData
        if (!currency || !hourlyRates) return 0

        let totalEarned = 0

        // Calculate earnings for each selected user
        usersToShow.forEach(userId => {
            const userHourlyRate = hourlyRates[userId]
            const userStats = statisticsData[userId]

            if (userHourlyRate && userHourlyRate > 0 && userStats?.doneTime > 0) {
                const timeInHours = userStats.doneTime / 60 // Convert minutes to hours
                totalEarned += timeInHours * userHourlyRate
            }
        })

        return totalEarned
    }

    const moneyEarned = calculateMoneyEarned()

    const calculateAllMoneyEarned = () => {
        if (!project?.hourlyRatesData?.hourlyRates) return {}

        const aggregated = {}
        const { hourlyRates } = project.hourlyRatesData

        Object.entries(allStatisticsData).forEach(([userId, userStats]) => {
            const userRate = hourlyRates[userId]
            const userAllDoneTime = userStats?.allDoneTime

            if (!userRate || !userAllDoneTime) return

            Object.entries(userAllDoneTime).forEach(([timestamp, hours]) => {
                const parsedHours = typeof hours === 'string' ? parseFloat(hours) : hours

                if (!parsedHours) return

                const earned = Number((parsedHours * userRate).toFixed(2))
                aggregated[timestamp] = (aggregated[timestamp] || 0) + earned
            })
        })

        return aggregated
    }

    const allMoneyEarned = calculateAllMoneyEarned()

    return (
        <View>
            <StatisticsSection
                projectId={projectId}
                updateFilterData={updateStatisticsFilter}
                statisticsData={statisticsDataReduced}
                allStatisticsData={{ ...allStatisticsDataReduced, allMoneyEarned }}
                statisticsFilter={filter}
                filterData={filterData}
                moneyEarned={moneyEarned}
                happinessEntries={happinessEntries}
                okrEntries={okrEntries}
            />
        </View>
    )
}
